/* The task strip (3.2) and the whole-video progress map (3.1.3).
 *
 * The map is one box per sub-task across the ENTIRE video, not per task. That
 * is what makes it a map: at a glance you see how much of the video is done and
 * where the rejects cluster, which a per-task view cannot show. */

import { useEffect, useRef, useState } from "react";
import type { Subtask, Task } from "../api/types";
import { clock, isResolved } from "../lib/format";
import { Eyebrow } from "./ui";

interface Props {
  tasks: Task[];
  currentTaskId: number;
  selectedSubtaskId: number | null;
  onPickTask: (task: Task) => void;
  onPickSubtask: (task: Task, sub: Subtask) => void;
}

function taskStatus(t: Task): { text: string; tone: string; pill: string } {
  const total = t.subtasks.length;
  const approved = t.subtasks.filter((s) => s.status === "APPROVED").length;
  const rejected = t.subtasks.filter((s) => s.status === "REJECTED").length;
  if (approved === total && total > 0)
    return { text: "✓ Complete", tone: "text-verdict-approved-ink",
             pill: "bg-verdict-approved-bg text-verdict-approved-ink ring-1 ring-verdict-approved-line" };
  if (t.subtasks.every(isResolved) && rejected)
    return { text: "Needs fixes", tone: "text-verdict-rejected-ink",
             pill: "bg-verdict-rejected-bg text-verdict-rejected-ink ring-1 ring-verdict-rejected-line" };
  return { text: "In review", tone: "text-muted",
           pill: "bg-verdict-flagged-bg text-verdict-flagged-ink ring-1 ring-verdict-flagged-line" };
}

/** The whole-video progress map: one box per sub-task, ACROSS THE VIDEO.
 *
 *  It lives in the workspace header now, between the approve/reject/pending
 *  counts and the ? — testing asked for it there so the row it used to occupy
 *  goes to the video.
 *
 *  ONE ROW THAT SCROLLS, NOT A WRAPPING BLOCK. It was `flex-wrap`, so
 *  skt_electrician_002's 222 sub-tasks became eight stacked rows of boxes and
 *  the map was the tallest thing on the screen. Wrapping also loses the one
 *  thing the map is for: with the boxes on a single line, position along the
 *  line IS position in the video.
 *
 *  It is deliberately narrow here. 222 boxes do not fit in a header slot and no
 *  layout makes them; what fits, fits, and the rest is a scroll away. The Next
 *  pending button is what makes that acceptable — it is the reason anyone
 *  scrolls this thing, so it does the scrolling for you. */
export function SubtaskMap(
  { tasks, selectedSubtaskId, onPick, onNextPending, className = "" }: {
    tasks: Task[]; selectedSubtaskId: number | null;
    onPick: (task: Task, sub: Subtask) => void;
    onNextPending: () => void;
    className?: string;
  },
) {
  const lane = useRef<HTMLDivElement>(null);

  // Keep the selected box in view as the annotator moves through the video —
  // otherwise the map silently stops describing where they are.
  useEffect(() => {
    const el = lane.current?.querySelector<HTMLElement>("[data-selected='1']");
    el?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [selectedSubtaskId]);

  const pending = tasks.flatMap((t) => t.subtasks)
    .filter((s) => s.status === "PENDING").length;

  return (
    /* FIXED SHAPE, WHATEVER THE SUB-TASK COUNT.
     *
     * Testing: "the lane bar length changes based on the number of sub-tasks,
     * please use a stable, fixed-width responsive layout and ensure the Next
     * Pending button remains consistently visible and accessible regardless of
     * the sub-task count."
     *
     * The lane is `flex-1 basis-0` so its width comes from the row, never from
     * its contents, and `min-w-0` lets it shrink instead of pushing. The button
     * is `shrink-0` and comes AFTER it in the DOM, so it is the last thing the
     * row will compress — it cannot be pushed out by a video with 90 sub-tasks.
     *
     * What was missing is the floor: with nothing to stop it, the lane collapses
     * toward zero on a narrow window and takes the dots with it. `min-w-24`
     * keeps a usable strip, and past that the row wraps rather than crushing
     * either the lane or the button. */
    /* SEVEN BOXES WIDE, SITTING WITH THE COUNTS.
     *
     * Testing, 17 Sep: "the statuses should be positioned before the sub-task
     * boxes" and "Next pending should be displayed next to where the grey boxes
     * end."
     *
     * Both were already true in the DOM — counts, then lane, then button — and
     * neither looked true, because the lane was `flex-1` and centred with
     * `mx-auto`. It took all the space between the counts and the right-hand
     * controls and floated its boxes in the middle of it, so the counts read as
     * detached on the left and the button as detached on the right.
     *
     * Content-width instead: `shrink` lets it give way on a narrow window,
     * `max-w` of seven boxes caps it, and with no `flex-1` it takes only what
     * the boxes need. The counts sit immediately before it and the button
     * immediately after, which is what was asked for and what the DOM already
     * said. `shrink-0` on the row stops the group being squeezed by the header.
     *
     * This replaces the `justify-center` centring from 16 Sep: with the lane no
     * wider than its contents there is nothing to centre within.
     *
     * CENTRING NOW HAPPENS ONE LEVEL UP (17 Sep). The whole cluster — counts,
     * these boxes, Next pending — is the middle column of the header's
     * `grid-cols-[1fr_auto_1fr]`, so it sits at the header's true midpoint.
     * That is a different thing from what was removed here: the lane still
     * takes only the width its boxes need, which is what keeps the counts
     * immediately before it and the button immediately after. Do not re-add
     * `flex-1` or `mx-auto` to this component to try to centre it — that
     * reintroduces exactly the detachment testing reported on 17 Sep. */
    /* gap-4 BEFORE "Next pending" — testing, 18 Sep. At gap-2 the button sat
       almost against the last box, so a lane that is scrolled to its end read
       as if it ran INTO the button. 16px separates the map from the control
       that moves through it. */
    <div className={`flex min-w-0 shrink-0 items-center gap-4 ${className}`}>
      {/* HOW MANY BOXES FIT, stated in the unit that decides it.
          A box is `w-3.5` (14px) and the gap is `0.5` (2px), so the pitch is
          exactly 16px = 1rem. `max-w-[28rem]` therefore shows 28 boxes, and the
          number in the class is the number on screen.

          The old cap read `calc(7*1.5rem)` — 168px — which sounds like seven
          boxes and actually drew ten, because 1.5rem was never the pitch. That
          is why "seven boxes" and what testing counted never agreed.

          Widened from ten to 28 on request, 17 Sep: a video with 70 sub-tasks
          showed ten of them, which is too thin a slice to be a map of anything.
          It is still a header strip and cannot show 70 — the lane scrolls, the
          selected box is kept in view, and Next pending does the scrolling for
          you. `shrink` means a narrow window takes this back first, before the
          counts or the buttons either side. */}
      {/* ROOM FOR THE SELECTION RING — testing, 18 Sep: "the outer box covering
          the grey boxes is not entirely visible".

          It was drawn and then clipped. The ring is `outline-2 outline-offset-1`,
          so it extends 3px OUTSIDE the box it marks; the lane had `py-1` but no
          horizontal padding, so the first box sat flush at x=700.8 with the
          container's own edge at x=700.8 and `overflow-x: auto` cut the left
          side of the ring off. Measured, not guessed.

          `px-1` gives it 4px at each end. `scroll-px-1` matters just as much and
          is easy to miss: `scrollIntoView({inline:"nearest"})` aligns to the
          PADDING edge, so without scroll-padding the ring would be clipped again
          the moment the lane scrolled to a selection — the static case fixed and
          the scrolled case still broken.

          `max-w` is 28.5rem rather than 28 to pay for those 8px, so the cap is
          still 28 boxes of content. The rule from the earlier round holds: the
          number here is the number ON SCREEN. */}
      <div ref={lane}
        className="lane-scroll flex min-w-24 max-w-[28.5rem] shrink
          gap-0.5 overflow-x-auto px-1 py-1 scroll-px-1">
        {tasks.map((t) =>
          t.subtasks.map((s) => {
            /* WHITE UNTIL IT IS DECIDED — testing, 18 Sep, and v1.6's `.bx`
               exactly: white inside with a grey border while pending, then the
               WHOLE box goes green or red once it has a verdict.

               Border AND background move together on a verdict, which is what
               makes the change read as the box filling in rather than as a box
               that merely changed colour. It also gives the strip its meaning
               at a glance: empty slots getting filled, not grey blocks. */
            const tone =
              s.status === "APPROVED"
                ? "border-verdict-approved bg-verdict-approved"
                : s.status === "REJECTED"
                  ? "border-verdict-rejected bg-verdict-rejected"
                  : "border-line bg-card";
            const selected = s.id === selectedSubtaskId;
            return (
              <button
                key={s.id}
                data-selected={selected ? "1" : "0"}
                onClick={() => onPick(t, s)}
                title={`T${t.task_index} · sub-task ${s.subtask_index} · ${s.status.toLowerCase()}`}
                // SQUARE, AND ROUNDER — testing, 18 Sep, matching v1.6's `.bx`
                // (13x13, border-radius 3.5px). Ours were 10x14: a rectangle
                // with barely any corner, which read as a tally mark rather
                // than as a thing you can click.
                //
                // 14px and not the deck's 13px, on purpose. A box is `w-3.5`
                // and the gap is `0.5`, so the pitch is exactly 16px = 1rem —
                // which is what lets `max-w-[28.5rem]` mean "28 boxes" and is
                // written into HANDOVER §5a. Going to 13px makes the pitch 15px
                // and quietly breaks that arithmetic for a difference nobody
                // can see. Keep the height at w-3.5 too, so it stays square.
                //
                // 3.5px sits below the radius ladder's smallest rung (key, 5px)
                // and should: the ladder's rule is that radius tracks size, and
                // this is the smallest thing the app draws.
                //
                // `border-[1.5px]` is v1.6's weight, and box-sizing keeps the
                // box at 14px with the border drawn inward — so the 16px pitch
                // that `max-w-[28.5rem]` depends on is untouched.
                //
                // NO `opacity-70` ANY MORE. It existed to soften solid grey
                // blocks; against a white fill it only fades the border, which
                // is the one thing telling you a pending box is there at all.
                // Hover darkens the border instead — visible on white, and
                // visible on a filled box too, which opacity was not.
                className={`size-3.5 shrink-0 rounded-[3.5px] border-[1.5px]
                  transition duration-150 ease-settle ${tone} ${
                  selected
                    ? "outline outline-2 outline-offset-1 outline-teal"
                    : "hover:border-muted"
                }`}
              />
            );
          }),
        )}
      </div>
      <button
        onClick={onNextPending}
        disabled={pending === 0}
        title={pending
          ? `Jump to the next sub-task with no verdict (${pending} left)`
          : "Every sub-task has a verdict"}
        className="shrink-0 rounded-control border border-line bg-card px-2 py-1
          text-[11px] text-text2 shadow-card hover:bg-ground
          disabled:opacity-40"
      >
        Next pending
      </button>
    </div>
  );
}

/** THE DISPLAY CAP FOR A TASK TITLE ON ITS TILE, in characters.
 *
 *  Titles come from the LLM grouper with no length limit of their own yet. On
 *  the live corpus (24 Sep, 176 tasks) they run 12–34 characters, median 22,
 *  so 36ch shows every one of them whole and only something longer than
 *  anything seen so far gets "…". When the grouper prompt gains a length
 *  limit, this should match it. `ch` is the width of "0" in the tile's own
 *  font, which runs wider than an average letter — so 36ch is a little more
 *  than 36 typical characters, never less. */
const TASK_TITLE_MAX_CH = 36;

/** The task tiles. The whole-video map used to sit above them; it is a header
 *  component now (SubtaskMap), which is a row of height back for the video. */
export function TaskStrip(p: Props) {
  return (
    <div className="flex flex-col gap-2">
      <div className="lane-scroll flex gap-2 overflow-x-auto pb-1">
        {p.tasks.map((t) => {
          // No `taskStatus(t)` here any more: the status word moved to the TASK
          // card with the timecodes, so the tile has nothing to colour by.
          const current = t.id === p.currentTaskId;
          return (
            <button
              key={t.id}
              onClick={() => p.onPickTask(t)}
              // ring-2 + ring-inset: a 1px ring sat half outside the rounded
              // corner and read as a broken outline on the selected tile.
              // Inset keeps the whole stroke inside the box it belongs to.
              // SIZED TO ITS TITLE, NOT A FIXED 224px — testing, 24 Sep: "task
              // name shouldn't have ... for shorter length". At w-56 the title
              // got ~15 characters beside T1 and the n/m pill, so even the
              // median title (22 chars on the live corpus) was cut. `w-max`
              // lets each tile be as wide as its title; the cap lives on the
              // title span below (TASK_TITLE_MAX_CH), not here.
              title={t.title}
              className={`w-max min-w-[8rem] shrink-0 rounded-card px-3 py-2 text-left transition
                ${current
                  ? "bg-ground ring-2 ring-inset ring-teal"
                  : "bg-card ring-1 ring-inset ring-line hover:bg-ground"}`}
            >
              <div className="flex items-baseline gap-2">
                <Eyebrow className="shrink-0">T{t.task_index}</Eyebrow>
                {/* BOLD — testing, 17 Sep. The title is the tile's subject;
                    it was the same weight as the timecode beneath it. */}
                <span className="min-w-0 truncate text-sm font-extrabold tracking-[-0.01em]
                  text-ink" style={{ maxWidth: `${TASK_TITLE_MAX_CH}ch` }}>
                  {t.title}
                </span>
                {/* HOW FAR THROUGH THIS TASK IS, on the tile.
                    The status word below says WHAT STATE it is in — "In review"
                    is true of a task with one sub-task decided and of one with
                    nineteen, so a strip of seven tiles all reading "In review"
                    tells an annotator nothing about where to go next. The
                    fraction does: decided over total, the same arithmetic the
                    Reviewed column uses in the queue. */}
                <span className="shrink-0 rounded-full bg-ground px-1.5 py-0.5
                  font-mono text-[11px] tabular-nums text-muted ring-1 ring-line2">
                  {t.subtasks.filter(isResolved).length}/{t.subtasks.length}
                </span>
              </div>
              {/* THE SECOND LINE IS GONE — testing, 18 Sep. It carried the
                  span, the duration and the status word, and all three now live
                  in the TASK card on the right, which is the panel that
                  describes the task you are actually on.

                  The tile keeps only what a TILE is for: which task it is, what
                  it is called, and how far through it you are. Seven of these
                  sit side by side, and repeating each one's timecodes across
                  all of them is detail you cannot use until you have picked
                  one — at which point the card opposite is already saying it.

                  It also buys the video a row of height back, which is the
                  scarcest thing on this screen. */}
            </button>
          );
        })}
      </div>
    </div>
  );
}


/* ------------------------------------------------------------- task card */

/** The CURRENT task, in full: what it is called, what happens in it, where you
 *  are in the video and how much of it is done.
 *
 *  Reported missing by testing, and it was: the strip above holds one tile per
 *  task, and a tile is 224px of truncated title. The DESCRIPTION — the sentence
 *  saying what the task actually covers — had nowhere to appear at all, so the
 *  only place to read it was the database. An annotator deciding whether a
 *  sub-task belongs to this task is the exact person who needs it.
 *
 *  ALWAYS SHOWN — requirement, 24 Sep. It used to be collapsed by default
 *  behind a ▼ toggle, to save the rows the workspace has little of (3.1.1),
 *  so the one sentence saying what the task covers was a click away from the
 *  person deciding whether a sub-task belongs in it. There is no toggle now:
 *  title row, then the description, always.
 *
 *  The title and description are NOT editable here. The prototype allows it;
 *  our API has no endpoint for it — `PATCH /subtasks/{id}` is sub-task fields
 *  only — and inventing one is a change to what an annotator may alter, not a
 *  UI detail. Read-only until that is decided.
 */
export function TaskCard(
  { task, index, total, onEdit, readOnly }: {
    task: Task; index: number; total: number; readOnly?: boolean;
    onEdit?: (patch: { title?: string; description?: string | null }) => void;
  },
) {
  const [editing, setEditing] = useState(false);
  const st = taskStatus(task);

  // shrink-0: it is the first row of a flex column whose second child (the
  // inspector) grows. Without it an expanded description would be compressed by
  // the panel below rather than pushing it down.
  //
  // p-3 matches the inspector it now stacks with, so the TASK and SUB-TASK
  // headings share an indent. At px-3 py-2 they were 4px out — close enough to
  // read as a mistake rather than as a margin.
  return (
    <div className="shrink-0 rounded-panel border border-line bg-card p-3">
      {/* The title row. It was a disclosure button that showed and hid the
          description; with the description always visible there is nothing
          to toggle, so it is plain content now and only the pencil is a
          control. */}
      <div className="flex items-center gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <Eyebrow className="shrink-0">Task</Eyebrow>
          {/* 14.5px / 800 / -0.01em — v1.6's `.taskcard .nm`, checked against
              the deck on 18 Sep. This was 14px at weight 700 with no tracking:
              Tier B gave every other heading in the app negative tracking and
              weight 800 and missed this one, so the TASK title was the only
              heading still set the old way. */}
          {/* TWO LINES, THEN "…" — testing, 24 Sep. `truncate` cut the title to
              one line beside the timecodes, the position and the status pill,
              so ordinary titles lost their last word. Wrapping to a second line
              shows every title in the current corpus whole; line-clamp is the
              cap for the rare longer one, and the tooltip has the full text. */}
          <span className="min-w-0 flex-1 line-clamp-2 break-words text-[14.5px]
            font-extrabold leading-snug tracking-[-0.01em] text-ink"
            title={task.title}>
            {task.title}
          </span>
          {/* THE SPAN IS BACK, and this time it belongs here — testing, 18 Sep
              moved it off the tiles and into this card.

              It was removed from this card once before, on the grounds that the
              tile above already showed it. That reasoning was sound while the
              tile carried it; now that the tile does not, this is the only place
              the task's own timecodes appear, and the card is the right one:
              it describes the task you are on rather than repeating the same
              detail across seven tiles you have not chosen yet.

              Mono, because it is a measurement. Same format the tile used, so
              nobody has to re-learn it. */}
          <span className="shrink-0 font-mono text-[11px] text-muted">
            {clock(task.start_seconds)}–{clock(task.end_seconds)}
            {" · "}{clock(task.end_seconds - task.start_seconds)}
          </span>
          <span className="shrink-0 text-[11px] font-semibold text-muted">
            {index + 1} of {total}
          </span>
          <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px]
            font-semibold ${st.pill}`}>{st.text}</span>
        </div>

        {!readOnly && onEdit && (
          <button
            onClick={() => setEditing(true)}
            title="Edit the task title and description"
            className="shrink-0 rounded-chip px-1 text-muted hover:text-ink"
          >✎</button>
        )}
      </div>

      {editing && onEdit ? (
        /* THE AI NAMED THESE WITHOUT RE-WATCHING. The grouper picks cut points
           from an event list and titles them from the same list, so a stretch
           where someone fetches a tool can come back as "Take a break" — a
           plausible reading of the events and wrong about the video. Until now
           there was no way to fix one: PATCH /subtasks edits sub-task fields,
           and nothing edited a task. */
        <div className="mt-2 flex flex-col gap-2"
          onClick={(e) => e.stopPropagation()}>
          <input
            autoFocus
            defaultValue={task.title}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
              if (e.key === "Escape") setEditing(false);
            }}
            onBlur={(e) => {
              const v = e.target.value.trim();
              // Empty is not an edit — it deletes the only label the strip and
              // this card both carry. The server refuses it too (422).
              if (v && v !== task.title) onEdit({ title: v });
            }}
            className="rounded-field border border-line bg-ground px-2 py-1 text-sm
              font-semibold text-ink"
            placeholder="Task title"
          />
          <textarea
            rows={2}
            defaultValue={task.description ?? ""}
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v !== (task.description ?? "")) onEdit({ description: v || null });
            }}
            className="resize-none rounded-field border border-line bg-ground px-2 py-1
              text-[11.5px] leading-relaxed text-text2"
            placeholder="What happens in this task, present tense"
          />
          <div className="flex justify-end">
            <button onClick={() => setEditing(false)}
              className="rounded-control bg-teal px-3 py-1 text-xs font-medium text-white
                hover:bg-teal7">Done</button>
          </div>
        </div>
      ) : (
        <p className="mt-1.5 text-[11.5px] leading-relaxed text-text2">
          {task.description?.trim()
            || "The AI grouped these sub-tasks together but gave the task no "
             + "description."}
        </p>
      )}
    </div>
  );
}
